import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { SpaceLayout } from '@/types/spaceLayout'

/*
 * F-198 "Exibição": how the space lays out its items, picked by owner and admin in the space's edit
 * and in "Criar Espaço". Only the layouts the module offers (from the API, and that the app draws);
 * each with a small drawing of the result. With fewer than two options there is nothing to choose
 * and the field is not drawn.
 */

interface SpaceLayoutFieldProps {
  options: SpaceLayout[]
  /** The chosen one; `null` is the module's default. */
  value: SpaceLayout | null
  defaultLayout: SpaceLayout
  onChange: (value: SpaceLayout) => void
  disabled?: boolean
  className?: string
}

const bar = 'rounded-sm bg-muted-foreground/30'

/** A drawing of each layout, about 64 × 40. */
const Preview = ({ layout }: { layout: SpaceLayout }) => {
  switch (layout) {
    case 'feed':
      return (
        <div className="flex h-10 w-16 flex-col gap-1">
          <span className={cn(bar, 'h-5 w-full')} />
          <span className={cn(bar, 'h-1 w-3/4')} />
          <span className={cn(bar, 'h-1 w-1/2')} />
        </div>
      )
    case 'cards':
      return (
        <div className="grid h-10 w-16 grid-cols-3 gap-1">
          {[0, 1, 2].map((i) => (
            <span key={i} className="flex flex-col gap-0.5">
              <span className={cn(bar, 'h-5')} />
              <span className={cn(bar, 'h-1')} />
            </span>
          ))}
        </div>
      )
    case 'list':
      return (
        <div className="flex h-10 w-16 flex-col justify-between">
          {[0, 1, 2].map((i) => (
            <span key={i} className="flex items-center gap-1">
              <span className={cn(bar, 'h-2.5 w-4')} />
              <span className={cn(bar, 'h-1 flex-1')} />
            </span>
          ))}
        </div>
      )
    case 'compact':
      return (
        <div className="flex h-10 w-16 flex-col justify-between py-0.5">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="flex items-center gap-1">
              <span className={cn(bar, 'h-1 flex-1')} />
              <span className={cn(bar, 'h-1 w-3')} />
            </span>
          ))}
        </div>
      )
    case 'grid':
      return (
        <div className="grid h-10 w-16 grid-cols-4 gap-0.5">
          {Array.from({ length: 8 }, (_, i) => <span key={i} className={cn(bar, 'aspect-square')} />)}
        </div>
      )
    case 'calendar':
      return (
        <div className="grid h-10 w-16 grid-cols-7 gap-px">
          {Array.from({ length: 21 }, (_, i) => <span key={i} className={cn('rounded-[1px]', i === 9 || i === 15 ? 'bg-primary-300' : 'bg-muted-foreground/20')} />)}
        </div>
      )
    case 'showcase':
      return (
        <div className="grid h-10 w-16 grid-cols-4 gap-1">
          {[0, 1, 2, 3].map((i) => <span key={i} className={cn(bar, 'h-full')} />)}
        </div>
      )
  }
}

export const SpaceLayoutField = ({ options, value, defaultLayout, onChange, disabled = false, className }: SpaceLayoutFieldProps) => {
  const { t } = useTranslation('common')
  if (options.length < 2) return null
  const current = value && options.includes(value) ? value : defaultLayout
  return (
    <fieldset className={cn('space-y-2', className)} data-testid="space-layout-field" disabled={disabled}>
      <legend className="text-sm font-medium text-foreground">{t('spaceLayout.label')}</legend>
      <p className="text-xs text-muted-foreground">{t('spaceLayout.hint')}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label={t('spaceLayout.label')}>
        {options.map((layout) => {
          const checked = layout === current
          return (
            <button
              key={layout}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => onChange(layout)}
              className={cn(
                'flex flex-col items-center gap-2 rounded-lg border px-2 py-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50',
                checked ? 'border-primary bg-primary-50 text-primary-700' : 'border-border bg-surface text-foreground hover:bg-surface-muted'
              )}
              data-layout-option={layout}
            >
              <Preview layout={layout} />
              <span className="font-medium">{t(`spaceLayout.options.${layout}`)}</span>
              {layout === defaultLayout && <span className="-mt-1.5 text-[11px] text-muted-foreground">{t('spaceLayout.default')}</span>}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
